if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.lockOption = this.form.querySelector('[data-toolbox-lock-option]');

        if (theme.settings.afterAtc !== 'no-js' || this.lockOption) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
          if (this.lockOption) {
            this.lockOption.disabled = false;
            this.lockPrice = this.closest('.product-info')?.querySelector('.product-info__price .price__current');
            this.lockComparePrice = this.closest('.product-info')?.querySelector('.product-info__price .price__was');
            if (this.lockPrice) {
              this.toolboxPriceHtml = this.lockPrice.innerHTML;
              this.toolboxComparePriceHtml = this.lockComparePrice?.innerHTML;
              this.lockOption.addEventListener('change', this.updateLockPrice.bind(this));
              this.updateLockPrice();
            }
          }
        }
      }
    }

    /** Updates the displayed price for the selected lock option. */
    updateLockPrice() {
      const addsLocks = this.lockOption.value === 'With Whale Tail Locks';
      this.lockPrice.innerHTML = addsLocks
        ? this.lockOption.dataset.priceWithLocks : this.toolboxPriceHtml;
      if (this.lockComparePrice) {
        this.lockComparePrice.innerHTML = addsLocks
          ? this.lockOption.dataset.comparePriceWithLocks : this.toolboxComparePriceHtml;
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const addsLocks = this.lockOption && this.lockOption.value === 'With Whale Tail Locks';
      let lockRequestSent = false;
      let addedSuccessfully = false;
      if (addsLocks) {
        const parentVariantId = formData.get('id');
        const quantity = Number(formData.get('quantity') || 1);
        formData.set('properties[_as_lock_group]', parentVariantId);
        Array.from(formData.entries()).forEach(([key, value]) => {
          if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) {
            formData.delete(key);
            formData.append(key.replace(/^([^[]+)(.*)$/, 'items[0][$1]$2'), value);
          }
        });
        formData.set('items[0][quantity]', quantity);
        formData.append('items[1][id]', this.lockOption.dataset.lockVariantId);
        formData.append('items[1][quantity]', quantity * 2);
        formData.append('items[1][properties][_as_lock_group]', parentVariantId);
        formData.append('items[1][properties][_as_lock_parent]', parentVariantId);
        formData.append('items[1][properties][_as_lock_ratio]', '2');
      }
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        lockRequestSent = addsLocks;
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        if (addsLocks) {
          const group = formData.get('items[0][id]');
          const groupedItems = newCartData.items.filter((item) => item.properties?._as_lock_group === group);
          const parentQuantity = groupedItems.filter((item) => !item.properties._as_lock_parent)
            .reduce((sum, item) => sum + item.quantity, 0);
          const lockQuantity = groupedItems.filter((item) => item.properties._as_lock_parent === group)
            .reduce((sum, item) => sum + item.quantity, 0);
          if (!parentQuantity || lockQuantity !== parentQuantity * 2) {
            this.setErrorMsgState(this.lockOption.dataset.lockAddError);
            throw new Error(this.lockOption.dataset.lockAddError);
          }
        }
        addedSuccessfully = true;

        if (theme.settings.afterAtc === 'page' || (this.lockOption && theme.settings.afterAtc === 'no-js')) {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const addedItems = data.items || [data];
        addedItems.forEach((addedItem) => {
          const itemInOldCart = oldCartData.items.find((item) => (
            item.variant_id === addedItem.variant_id
            && (!this.lockOption || (
              item.properties?.['Lock option'] === addedItem.properties?.['Lock option']
              && item.properties?._as_lock_group === addedItem.properties?._as_lock_group
              && item.properties?._as_lock_parent === addedItem.properties?._as_lock_parent
            ))
          ));

          if (itemInOldCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: addedItem.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: addedItem.quantity
              }
            }));
          } else {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: addedItem.variant_id
              }
            }));
          }
        });
      } catch (error) {
        if (lockRequestSent) {
          this.setErrorMsgState([
            this.errorMsg?.textContent || theme.strings.cartError,
            this.lockOption.dataset.lockAddError
          ]);
          try {
            const sectionResponse = await fetch(`?sections=${sections}`);
            if (!sectionResponse.ok) throw new Error(sectionResponse.status);
            const liveSections = await sectionResponse.json();
            ProductForm.updateCartIcon({ sections: liveSections });
            if (this.cartDrawer) await this.cartDrawer.renderContents({ sections: liveSections }, true);
          } catch (refreshError) {
            console.log(refreshError); // eslint-disable-line
          }
        }
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent || theme.strings.cartError
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        if (!this.lockOption || addedSuccessfully) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
